import { useCallback, useSyncExternalStore } from 'react';

import { featureFlagClient } from './core/featureFlagClient';
import type { FeatureFlagProvider } from './core/featureFlagClient.types';
import type {
  FeatureFlagKey,
  FeatureFlagKind,
  FeatureFlagValue,
  PortalFeatureFlagDefinition,
} from './featureFlagDefinition';
import { getFeatureFlagValue } from './getFeatureFlagValue';

type AnyPortalFeatureFlagDefinition = PortalFeatureFlagDefinition<
  FeatureFlagKey,
  FeatureFlagProvider,
  FeatureFlagKind
>;

export function useFeatureFlagValue<Definition extends AnyPortalFeatureFlagDefinition>(
  definition: Definition,
  defaultValue: FeatureFlagValue<Definition>
): FeatureFlagValue<Definition> {
  const subscribe = useCallback(
    (onStoreChange: () => void) =>
      featureFlagClient.onChange(onStoreChange, { provider: definition.provider }),
    [definition.provider]
  );
  const getSnapshot = useCallback(
    () => getFeatureFlagValue(definition, defaultValue),
    [defaultValue, definition]
  );
  const getServerSnapshot = useCallback(() => defaultValue, [defaultValue]);

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
